/**
 * claudeOcrService.js
 * ===================
 * Håndterer OCR via Claude Vision API for dokument-analyse.
 *
 * NY FUNKTIONALITET:
 * - Kategoriserer dokumenter automatisk (grundlag, kreditnoter, rykkere, øvrig)
 * - Bestemmer sagstype baseret på dokumentindhold
 * - Udtrækker alle relevante felter inkl. skyldner-info
 * - Returnerer confidence scores
 *
 * Claude Vision er markant bedre end traditionel OCR til at:
 * - Forstå dokument-layouts uanset format
 * - Identificere relevante felter korrekt
 * - Håndtere varierende kvalitet og formater
 * - Kategorisere dokumenttyper præcist
 *
 * SIKKERHED: Kaldet går gennem en nginx-proxy på samme host som denne app
 * (se docs/claude-proxy.md). API-nøglen ligger kun på serveren i
 * /etc/nginx/claude-key.conf — den må aldrig lægges i en VUE_APP_-variabel, da
 * alt med det præfiks bygges ind i det offentligt læsbare JS-bundle.
 *
 * Omkostninger: ~$0.003-0.015 per dokument (afhængig af billedstørrelse)
 */

import { ref } from '@vue/composition-api'

// ============================================================
// CONFIGURATION
// ============================================================

// Claude tilgås gennem vores egen nginx-proxy på samme origin.
// API-nøglen ligger udelukkende på serveren og når aldrig frontend-bundlet.
const CLAUDE_PROXY_URL = process.env.VUE_APP_CLAUDE_PROXY_URL || '/claude/v1/messages'

// Helbredsendpoint på proxyen. Svarer 204 og kræver hverken login eller
// tokens — bruges kun til at afgøre, om AI-funktionen skal vises.
const CLAUDE_HEALTH_URL = process.env.VUE_APP_CLAUDE_HEALTH_URL || '/claude/health'

/**
 * Headers til proxy-kaldet.
 *
 * Brugerens eget JWT sendes med, så proxyen kan afvise kald fra ikke-loggede
 * brugere. Uden det ville proxyen være et åbent Claude-relæ, som hvem som helst
 * kunne bruge på vores regning. Proxyen fjerner denne header, inden requestet
 * går videre til Anthropic.
 */
function claudeHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
  }
}

/**
 * Reaktiv status for AI-analyse, delt på tværs af komponenter.
 *
 *   'unknown'     - endnu ikke afprøvet, vi antager den virker
 *   'available'   - et kald er lykkedes
 *   'unavailable' - proxyen eller Anthropic svarer ikke; UI skal falde
 *                   tilbage til manuel udfyldning
 *
 * Formålet er, at brugeren ikke bliver ved med at få tilbudt AI-analyse,
 * når den ikke kan gennemføres.
 */
const claudeStatus = ref('unknown')

/**
 * Tjekker om AI-analyse overhovedet kan forsøges.
 */
function isClaudeAvailable() {
  return !!localStorage.getItem('accessToken') && claudeStatus.value !== 'unavailable'
}

/**
 * Markerer AI som utilgængelig for resten af sessionen.
 *
 * Kaldes kun ved infrastrukturfejl — proxy nede, gate afviser, netværk væk.
 * En fejl i ét enkelt dokument (ulæselig PDF, uparsbart svar) må ikke slå
 * AI fra for alle andre dokumenter.
 */
function markClaudeUnavailable(reason) {
  if (claudeStatus.value !== 'unavailable') {
    console.warn(`AI-analyse slået fra for denne session: ${reason}`)
  }
  claudeStatus.value = 'unavailable'
}

/**
 * Oversætter et HTTP-svar fra proxyen til enten en infrastrukturfejl
 * (slår AI fra) eller en almindelig fejl (rører ikke status).
 */
function handleProxyFailure(status) {
  if (status >= 500) {
    markClaudeUnavailable(`proxy/API svarede ${status}`)
    return 'AI-analyse er ikke tilgængelig lige nu. Udfyld felterne manuelt.'
  }
  if (status === 401 || status === 403) {
    markClaudeUnavailable(`adgang nægtet (${status})`)
    return 'Din session er udløbet. Log ind igen for at bruge AI-analyse.'
  }
  return `API fejl: ${status}`
}

/**
 * Billigt tjek af om proxyen overhovedet er sat op på serveren.
 *
 * Rammer et dedikeret helbredsendpoint, der svarer 204 uden indhold. Er
 * nginx-delen ikke lagt ind, fanger SPA-fallbacken stien og svarer 200 med
 * index.html — og så ved vi, at AI ikke kan bruges.
 *
 * Kaldet når aldrig Anthropic og koster derfor ingen tokens.
 */
async function probeClaudeProxy() {
  try {
    const response = await fetch(CLAUDE_HEALTH_URL, { method: 'GET', cache: 'no-store' })
    if (response.status === 204) return true
    markClaudeUnavailable(`proxy ikke konfigureret (helbredstjek gav ${response.status})`)
    return false
  } catch (error) {
    markClaudeUnavailable(`proxyen kunne ikke nås: ${error.message}`)
    return false
  }
}

// Claude model til vision-opgaver
const CLAUDE_MODEL = 'claude-sonnet-5'

// Antal PDF-sider der sendes med. Dækker faktura + specifikationer uden at
// lade en fejlagtigt uploadet kontrakt på 60 sider koste en formue.
const MAX_PDF_PAGES = 5

/**
 * Fælles regel for at skelne debitor fra kreditor.
 *
 * Den tidligere formulering slog fast, at debitor "ALTID" står i øverste
 * venstre hjørne, og at højre side skulle ignoreres. Det er ikke rigtigt —
 * mange danske fakturaer sætter afsender til venstre og modtager til højre.
 * På sådan et dokument fulgte modellen reglen og udtrak kreditoren som
 * skyldner, med høj selvtillid. I et inkassosystem betyder det en sag mod
 * den forkerte part.
 *
 * Reglen er derfor rollebaseret: pengestrømmen afgør, og placering er kun
 * et hjælpespor.
 */
const DEBTOR_RULE = `IDENTIFIKATION AF DEBITOR (SKYLDNER):
Du skal finde DEBITOR — den part der SKAL BETALE — og ikke kreditor, som skal
modtage pengene. Afgør det på rolle og pengestrøm, ikke på placering.

Kendetegn ved KREDITOR (skal IKKE bruges som debitor):
- Har logo eller grafisk brevhoved
- Står ved betalingsoplysninger: bankkonto, reg./kontonr., IBAN, FIK-kode,
  MobilePay, "Betaling til", "Beløbet indsættes på"
- Har kontaktinfo til spørgsmål om fakturaen ("Ved spørgsmål kontakt...")
- Nævnes i handelsbetingelser eller footer

Kendetegn ved DEBITOR (det er denne du skal udtrække):
- Er den fakturaen er stilet TIL: "Til:", "Kunde", "Fakturamodtager",
  "Deres ref.", "Kundenr."
- Står ofte i brevrudeposition, typisk øverste venstre hjørne — men det er
  KUN et hjælpespor. Nogle layouts har modtager til højre eller centreret.
- Er den part, teksten pålægger at betale

Fremgangsmåde:
1. Find betalingsoplysningerne. Den part, pengene skal TIL, er kreditor.
2. Den ANDEN navngivne part er debitor.
3. Er der flere CVR-numre, hører det ved kreditors betalingsoplysninger til
   kreditor — det andet til debitor.
4. Kan du ikke afgøre det entydigt, så sæt debitor.confidence lavt og skriv
   hvorfor i "warnings". Gæt ikke.

Vær opmærksom: dokumentet kan være udstedt af et inkasso- eller
faktureringsbureau på vegne af kreditor. Bureauet er hverken debitor eller
kreditor — se efter "på vegne af".`

// Renderingsskala. 2.0 giver ~1190x1684 px på A4. Sonnet 5 læser op til
// 2576 px på den lange kant, og 2.6 rammer ~1550x2190 — mærkbart bedre på
// små typer og stemplet tekst uden at ramme loftet.

// Renderingsskala. 2.0 giver ~1190x1684 px på A4. Sonnet 5 læser op til
// 2576 px på den lange kant, og 2.6 rammer ~1550x2190 — mærkbart bedre på
// små typer og stemplet tekst uden at ramme loftet.
const PDF_RENDER_SCALE = 2.6

// Sonnet 5 kører adaptive thinking som standard, hvis 'thinking' udelades.
// max_tokens dækker thinking + svar tilsammen, så uden dette ville JSON-svaret
// kunne blive afkortet midt i. Vi vil kun have ekstraktion, ikke ræsonnement.
const CLAUDE_THINKING = { type: 'disabled' }

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Konverterer en fil til base64 data URL.
 * Bruges til at sende billedet til Claude API.
 */
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      // Fjern data URL prefix og returner kun base64 data
      const base64 = reader.result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Renderer én PDF-side til en PNG-blob.
 */
async function renderPdfPage(pdf, pageNumber) {
  const page = await pdf.getPage(pageNumber)
  const viewport = page.getViewport({ scale: PDF_RENDER_SCALE })

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  canvas.height = viewport.height
  canvas.width = viewport.width

  await page.render({ canvasContext: context, viewport }).promise

  return new Promise(resolve => {
    canvas.toBlob(blob => resolve(blob), 'image/png')
  })
}

/**
 * Konverterer PDF til billeder vha. PDF.js. Claude kræver billeder, ikke PDF.
 *
 * Læser op til MAX_PDF_PAGES sider. Tidligere blev kun side 1 sendt, hvilket
 * betød at specifikationer, rykkerhistorik og totaler på senere sider var
 * usynlige for modellen — uden at nogen kunne se det på resultatet.
 */
async function pdfToImageBlobs(file) {
  const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf')
  pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/legacy/build/pdf.worker.min.js'

  let pdf
  try {
    const buffer = await file.arrayBuffer()
    pdf = await pdfjsLib.getDocument(new Uint8Array(buffer)).promise
  } catch (error) {
    throw new Error(`Kunne ikke læse PDF: ${error.message}`)
  }

  const renderedPages = Math.min(pdf.numPages, MAX_PDF_PAGES)
  const blobs = []

  for (let pageNumber = 1; pageNumber <= renderedPages; pageNumber += 1) {
    // Siderne renderes bevidst sekventielt — PDF.js deler ét worker-thread,
    // og parallelle render-kald giver ingen gevinst her.
    // eslint-disable-next-line no-await-in-loop
    blobs.push(await renderPdfPage(pdf, pageNumber))
  }

  return { blobs, totalPages: pdf.numPages, renderedPages }
}

/**
 * Gør en uploadet fil klar som billed-blokke til Claude.
 *
 * Returnerer også sidetal, så prompten kan fortælle modellen, hvis dokumentet
 * er længere end det, den får at se.
 */
async function buildImageBlocks(file) {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

  if (!isPdf) {
    return {
      blocks: [{
        type: 'image',
        source: {
          type: 'base64',
          media_type: file.type || 'image/png',
          data: await fileToBase64(file),
        },
      }],
      totalPages: 1,
      renderedPages: 1,
    }
  }

  const { blobs, totalPages, renderedPages } = await pdfToImageBlobs(file)
  const blocks = await Promise.all(blobs.map(async blob => ({
    type: 'image',
    source: {
      type: 'base64',
      media_type: blob.type || 'image/png',
      data: await fileToBase64(blob),
    },
  })))

  return { blocks, totalPages, renderedPages }
}

/**
 * Sætning der fortæller modellen, hvor mange sider den ser — så den ikke
 * konkluderer på et ufuldstændigt dokument uden at nævne det.
 */
function pageContextNote(totalPages, renderedPages) {
  if (totalPages <= 1) return ''
  if (renderedPages >= totalPages) {
    return `\n\nDokumentet har ${totalPages} sider, og du får dem alle som billeder i rækkefølge.`
  }
  return `\n\nDokumentet har ${totalPages} sider, men du får kun de første ${renderedPages}. `
    + 'Hvis et beløb eller en total tydeligvis står på en side, du ikke kan se, så sæt feltet til null '
    + 'og skriv det i "warnings" i stedet for at gætte.'
}

/**
 * Normaliserer datoformat til YYYY-MM-DD
 */
function normalizeDate(dateStr) {
  if (!dateStr) return null

  // Hvis det allerede er i YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr
  }

  // Prøv at parse DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY
  const dmyMatch = dateStr.match(/(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/)
  if (dmyMatch) {
    let [, day, month, year] = dmyMatch
    day = parseInt(day, 10)
    month = parseInt(month, 10)
    year = parseInt(year, 10)

    // 2-cifret år til 4-cifret
    if (year < 100) {
      year = year > 50 ? 1900 + year : 2000 + year
    }

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  }

  return null
}

/**
 * Normaliserer en confidence-værdi fra modellen til 0-100.
 *
 * Returnerer null hvis modellen ikke gav et brugbart tal — så kan UI'et lade
 * være med at vise en procent frem for at opfinde en.
 */
function normalizeConfidence(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return null

  return Math.min(100, Math.max(0, Math.round(number)))
}

/**
 * Parser Claude's JSON-respons.
 * Claude returnerer JSON i et markdown code block.
 */
function parseClaudeResponse(responseText) {
  // Prøv at finde JSON i code block
  const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/)
  if (jsonMatch) {
    try {
      return JSON.parse(jsonMatch[1].trim())
    } catch (e) {
      console.warn('Kunne ikke parse JSON fra code block:', e)
    }
  }

  // Prøv at parse hele responsen som JSON
  try {
    return JSON.parse(responseText)
  } catch (e) {
    console.warn('Kunne ikke parse respons som JSON:', e)
  }

  return null
}

// ============================================================
// MAIN OCR FUNCTION
// ============================================================

/**
 * Udfører OCR på en faktura vha. Claude Vision API.
 *
 * @param {File} file - PDF eller billedfil der skal analyseres
 * @param {Function} onProgress - Callback for progress updates (0-100)
 * @returns {Object} Ekstraherede data
 */
async function extractInvoiceDataWithClaude(file, onProgress = () => {}) {
  if (!isClaudeAvailable()) {
    return {
      success: false,
      error: 'Du er ikke logget ind. Log ind igen for at bruge AI-analyse.',
      data: null,
    }
  }

  try {
    onProgress(10)

    onProgress(20)
    const { blocks, totalPages, renderedPages } = await buildImageBlocks(file)
    onProgress(45)

    // Prompt til Claude - struktureret for at få konsistent JSON output
    const prompt = `Analyser denne faktura og udtræk følgende information. Svar KUN med JSON i det eksakte format nedenfor.

Vigtige instruktioner:
- Fakturanummer: Find et unikt nummer der identificerer fakturaen
- Fakturadato: Datoen fakturaen blev udstedt
- Forfaldsdato: Datoen betalingen skal ske senest
- Totalbeløb: Det FULDE beløb inkl. moms der skal betales
- Rentesats: Hvis dokumentet angiver en rentesats (fx "rente", "renter", "rentesats" efterfulgt af en procentangivelse), udtræk den som et tal

${DEBTOR_RULE}

Returner JSON i præcis dette format:
\`\`\`json
{
  "invoiceNumber": "string eller null",
  "invoiceDate": "DD-MM-YYYY eller null",
  "dueDate": "DD-MM-YYYY eller null",
  "totalAmount": number eller null,
  "interestRate": number eller null,
  "dataConfidence": 0-100,
  "debtor": {
    "cvr": "string (8 cifre) eller null",
    "companyName": "string eller null",
    "address": "string eller null",
    "houseNumber": "string eller null",
    "zipCode": "string (4 cifre) eller null",
    "city": "string eller null",
    "confidence": 0-100
  },
  "warnings": ["liste af advarsler hvis noget er tvetydigt"]
}
\`\`\`

"dataConfidence" er din reelle vurdering af, hvor sikre de udtrukne FELTVÆRDIER er
— ikke et høflighedstal. Er dokumentet sløret, håndskrevet, beskåret eller
tvetydigt, skal tallet være lavt.

Hvis du ikke kan finde en værdi, brug null. Beløb skal være tal uden valutasymboler.${pageContextNote(totalPages, renderedPages)}`

    // Kald Claude API
    onProgress(50)
    const response = await fetch(CLAUDE_PROXY_URL, {
      method: 'POST',
      headers: claudeHeaders(),
      body: JSON.stringify({
        model: CLAUDE_MODEL,
        max_tokens: 2048,
        thinking: CLAUDE_THINKING,
        messages: [
          {
            role: 'user',
            content: [
              ...blocks,
              {
                type: 'text',
                text: prompt,
              },
            ],
          },
        ],
      }),
    })

    onProgress(85)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      // Klassificeringen skal altid køre. Stod handleProxyFailure til højre
      // for ||, ville den blive sprunget over, når API'et selv sendte en
      // besked med — og så ville AI aldrig blive slået fra ved infrastrukturfejl.
      const statusMessage = handleProxyFailure(response.status)
      throw new Error(errorData.error?.message || statusMessage)
    }

    claudeStatus.value = 'available'

    const result = await response.json()
    onProgress(95)

    // Parse Claude's respons
    const responseText = result.content[0]?.text || ''
    const parsedData = parseClaudeResponse(responseText)

    if (!parsedData) {
      throw new Error('Kunne ikke parse Claude\'s respons')
    }

    // Normaliser data til vores format
    const extractedData = {
      title: parsedData.invoiceNumber ? `Faktura ${parsedData.invoiceNumber}` : null,
      invoiceDate: normalizeDate(parsedData.invoiceDate),
      dueDate: normalizeDate(parsedData.dueDate),
      totalAmount: parsedData.totalAmount,
      interestRate: parsedData.interestRate,
      debtor: parsedData.debtor,
    }

    onProgress(100)

    console.log('Claude OCR extracted data:', extractedData)

    return {
      success: true,
      data: extractedData,
      rawResponse: responseText,
      // Modellens egen vurdering af feltværdierne. Var tidligere hardkodet
      // til 95 med kommentaren "Claude er generelt meget præcis" — altså et
      // tal uden dækning, som brugeren læste som en måling.
      confidence: normalizeConfidence(parsedData.dataConfidence),
      debtorConfidence: normalizeConfidence(parsedData.debtor?.confidence),
      warnings: parsedData.warnings || [],
      pages: { total: totalPages, analyzed: renderedPages },
    }
  } catch (error) {
    console.error('Claude OCR fejl:', error)
    return {
      success: false,
      error: error.message,
      data: null,
    }
  }
}

// ============================================================
// NEW: DOCUMENT ANALYSIS WITH CATEGORIZATION
// ============================================================

/**
 * Analyserer et dokument og kategoriserer det automatisk.
 * Udtrækker alle relevante felter baseret på dokumenttype.
 *
 * @param {File} file - Dokument der skal analyseres
 * @param {Function} onProgress - Callback for progress updates (0-100)
 * @returns {Object} Analyse resultat med kategori, data og confidence
 */
async function analyzeDocumentWithClaude(file, onProgress = () => {}) {
  if (!isClaudeAvailable()) {
    return {
      success: false,
      error: 'Du er ikke logget ind. Log ind igen for at bruge AI-analyse.',
      category: null,
      data: null,
      confidence: 0,
    }
  }

  try {
    onProgress(5)

    onProgress(10)
    const { blocks, totalPages, renderedPages } = await buildImageBlocks(file)
    onProgress(45)

    // Udvidet prompt til kategorisering og data-udtræk
    const prompt = `Analyser dette dokument og kategoriser det. Svar KUN med JSON i det eksakte format nedenfor.

DOKUMENTKATEGORIER:
1. "grundlag" - Fakturaer, regninger, domme, gældsbreve, fundament for pengekrav
2. "kreditnoter" - Kreditnotaer, tilbagebetalinger, nedskrivninger
3. "rykkere" - Rykkerskrivelser, betalingspåmindelser, inkassovarsler
4. "oevrig" - Øvrig dokumentation (kontrakter, korrespondance, etc.)

SAGSTYPER (bestem baseret på grundlag):
1 = Faktura (standard faktura/regning)
2 = Dom (retsafgørelse, fogedret)
3 = Gældsbrev (underskrevet gældsbrev)
4 = Andet (øvrige kravtyper)

VIGTIGE REGLER FOR RYKKERE (dansk inkassolovgivning):
1. RYKKERGEBYR: Må ALDRIG overstige 100 kr per rykker. Hvis dokumentet viser et højere gebyr, sæt reminderFee til 100.
2. KOMPENSATIONSGEBYR: Er ALTID præcis 310 kr eller 0 kr - ingen andre værdier. Sæt hasCompensationFee til true hvis 310 kr kompensationsgebyr fremgår tydeligt.
3. RENTER: Udtræk KUN renter der er påløbet for DENNE rykker. Medtag IKKE akkumulerede renter fra tidligere rykkere.
   - Hvis dokumentet viser "renter i alt" eller "samlet rente", skal du identificere hvad der er NYE renter for denne periode.
   - Tidligere renter (fra rykker 1, rykker 2 osv.) må IKKE medtages igen.
4. ADSKIL rykkergebyr, kompensationsgebyr og renter - de må IKKE forveksles.

${DEBTOR_RULE}

INSTRUKTIONER:
- Kategorisér dokumentet korrekt
- Udtræk ALLE relevante felter
- Datoer i DD-MM-YYYY format
- Beløb som tal uden valutasymboler
- For rykkere: Vær særlig opmærksom på at adskille gebyr, kompensation og renter korrekt
- DOBBELTTJEK debitor: fulgte du pengestrømmen, eller gik du bare efter placering?

CONFIDENCE-FELTER — angiv tre uafhængige vurderinger, og vær ærlig:
- "categoryConfidence": hvor sikker er kategoriseringen
- "dataConfidence": hvor sikre er de udtrukne BELØB OG DATOER
- "debtor.confidence": hvor sikker er du på, at du valgte den rigtige part

Et lavt tal er en brugbar oplysning. Et højt tal, der ikke holder, får en
sagsbehandler til at springe kontrollen over. Er dokumentet sløret, beskåret,
håndskrevet eller tvetydigt, skal tallene være lave.

Returner JSON:
\`\`\`json
{
  "category": "grundlag|kreditnoter|rykkere|oevrig",
  "categoryConfidence": 0-100,
  "caseType": 1|2|3|4,
  "caseTypeConfidence": 0-100,
  "dataConfidence": 0-100,
  "data": {
    "title": "Fakturanummer eller dokumenttitel",
    "invoiceDate": "DD-MM-YYYY eller null",
    "dueDate": "DD-MM-YYYY eller null",
    "documentDate": "DD-MM-YYYY eller null",
    "totalAmount": number eller null,
    "interestRate": number eller null,
    "creditNoteAmount": number eller null,
    "reminderFee": number eller null (max 100 kr),
    "hasCompensationFee": boolean (true hvis 310 kr kompensationsgebyr fremgår),
    "interestAmount": number eller null (KUN renter for denne rykker, ikke akkumulerede),
    "amountPaid": number eller null
  },
  "debtor": {
    "cvr": "8 cifre eller null",
    "cpr": "10 cifre uden bindestreg eller null",
    "companyName": "string eller null",
    "personName": "string eller null",
    "address": "vejnavn eller null",
    "houseNumber": "string eller null",
    "zipCode": "4 cifre eller null",
    "city": "string eller null",
    "phone": "string eller null",
    "email": "string eller null",
    "confidence": 0-100
  },
  "warnings": ["liste af advarsler hvis noget ser usædvanligt ud, fx høje renter eller tvivl om hvem der er debitor"]
}
\`\`\`

Hvis et felt ikke kan findes, brug null.${pageContextNote(totalPages, renderedPages)}`

    // Kald Claude API med simuleret progress animation
    onProgress(50)

    // Start simuleret progress mens vi venter på API (50% -> 85% over ~8 sekunder)
    let simulatedProgress = 50
    const progressInterval = setInterval(() => {
      if (simulatedProgress < 85) {
        simulatedProgress += 2 // +2% hver 500ms = 35% over ~8.75 sek
        onProgress(simulatedProgress)
      }
    }, 500)

    let response
    try {
      response = await fetch(CLAUDE_PROXY_URL, {
        method: 'POST',
        headers: claudeHeaders(),
        body: JSON.stringify({
          model: CLAUDE_MODEL,
          max_tokens: 4096,
          thinking: CLAUDE_THINKING,
          messages: [
            {
              role: 'user',
              content: [
                ...blocks,
                {
                  type: 'text',
                  text: prompt,
                },
              ],
            },
          ],
        }),
      })
    } finally {
      // Stop simuleret progress når API svarer
      clearInterval(progressInterval)
    }

    onProgress(88)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      // Klassificeringen skal altid køre. Stod handleProxyFailure til højre
      // for ||, ville den blive sprunget over, når API'et selv sendte en
      // besked med — og så ville AI aldrig blive slået fra ved infrastrukturfejl.
      const statusMessage = handleProxyFailure(response.status)
      throw new Error(errorData.error?.message || statusMessage)
    }

    claudeStatus.value = 'available'

    const result = await response.json()
    onProgress(95)

    // Parse Claude's respons
    const responseText = result.content[0]?.text || ''
    const parsedData = parseClaudeResponse(responseText)

    if (!parsedData) {
      throw new Error('Kunne ikke parse Claude\'s respons')
    }

    // Normaliser datoer
    if (parsedData.data) {
      parsedData.data.invoiceDate = normalizeDate(parsedData.data.invoiceDate)
      parsedData.data.dueDate = normalizeDate(parsedData.data.dueDate)
      parsedData.data.documentDate = normalizeDate(parsedData.data.documentDate)
    }

    onProgress(100)

    console.log('Claude document analysis:', parsedData)

    return {
      success: true,
      category: parsedData.category || 'oevrig',
      // Bemærk: 'confidence' er kategoriseringens sikkerhed. Feltværdiernes
      // sikkerhed er 'dataConfidence' — de to blev tidligere blandet sammen
      // i UI'et, så en sikker kategorisering blev vist som om felterne var
      // lige så sikre.
      confidence: normalizeConfidence(parsedData.categoryConfidence),
      dataConfidence: normalizeConfidence(parsedData.dataConfidence),
      debtorConfidence: normalizeConfidence(parsedData.debtor?.confidence),
      caseType: parsedData.caseType || 4,
      caseTypeConfidence: normalizeConfidence(parsedData.caseTypeConfidence),
      data: parsedData.data || {},
      debtor: parsedData.debtor || null,
      warnings: parsedData.warnings || [],
      pages: { total: totalPages, analyzed: renderedPages },
      rawResponse: responseText,
    }
  } catch (error) {
    console.error('Claude document analysis fejl:', error)
    return {
      success: false,
      error: error.message,
      category: null,
      data: null,
      confidence: 0,
    }
  }
}

// ============================================================
// EXPORTS
// ============================================================

export {
  extractInvoiceDataWithClaude,
  analyzeDocumentWithClaude,
  isClaudeAvailable,
  claudeStatus,
  probeClaudeProxy,
  markClaudeUnavailable,
}

export default {
  extractInvoiceDataWithClaude,
  analyzeDocumentWithClaude,
  isClaudeAvailable,
  claudeStatus,
  probeClaudeProxy,
  markClaudeUnavailable,
}
